import { Button, Flex, Skeleton, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import Icon from "../../Icon"
import { GLOBAL_MAP_SURFACE_CLASS } from "./global-map-surface.ts"
import { relativeTime } from "./helpers/relative-time.ts"
import { useRecentActivityInView } from "./hooks/useRecentActivityInView.ts"
import { EntityGlyphTile } from "./map/EntityGlyphTile.tsx"
import type {
  AnalyticsRecentActivityEvent,
  EntityType
} from "./redux/global-map.types"

const { Title, Text } = Typography

// An activity card hovers toward the entity its column is about, rather than
// toward one shared blue — the same Lead-blue/Project-green grammar the
// markers, the legend and the layer chips already use. Whole class strings
// because Tailwind only sees literals.
const CARD_HOVER_CLASS: Record<EntityType, string> = {
  lead: "hover:border-blue-300 hover:bg-blue-50/50",
  project: "hover:border-green-300 hover:bg-green-50/50"
}

interface RecentActivityInViewProps {
  onSelectEvent: (event: AnalyticsRecentActivityEvent) => void
  onViewAll: () => void
}

interface EntityColumnProps {
  entityType: EntityType
  heading: string
  emptyLabel: string
  events: AnalyticsRecentActivityEvent[]
  onSelectEvent: (event: AnalyticsRecentActivityEvent) => void
}

function EntityColumn({
  entityType,
  heading,
  emptyLabel,
  events,
  onSelectEvent
}: EntityColumnProps) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:px-4 first:sm:pl-0 last:sm:pr-0">
      <div className="flex items-center gap-1.5">
        <EntityGlyphTile entityType={entityType} size={18} />
        <Text className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {heading}
        </Text>
      </div>
      {events.length === 0 ? (
        <div className="flex min-h-[64px] items-center justify-center rounded-md border border-dashed border-slate-200 px-2 py-3">
          <Text type="secondary" className="text-xs">
            {emptyLabel}
          </Text>
        </div>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row">
          {events.map((event, index) => (
            <button
              key={`${event.recordId}-${event.timestamp}-${index}`}
              type="button"
              onClick={() => onSelectEvent(event)}
              className={`flex min-w-0 flex-1 items-start gap-2.5 rounded-lg border border-slate-200 bg-white p-3 text-left shadow-[0_1px_1px_rgba(0,0,0,0.04)] transition-colors ${CARD_HOVER_CLASS[entityType]}`}
            >
              <EntityGlyphTile entityType={entityType} size={36} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[11px] font-medium text-slate-500">
                  {event.description}
                </div>
                <div className="truncate text-sm font-semibold leading-snug text-slate-900">
                  {event.recordTitle}
                </div>
                <div className="truncate text-[11px] text-slate-400">
                  {relativeTime(event.timestamp)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * "Recent Activity in View" — beneath the map column. Reuses the same
 * aggregate computation the Analytics tab's own Recent Activity card is
 * built on (`compute_recent_activity_by_entity`, a light dedicated endpoint
 * rather than the full `compute_analytics()` — see that endpoint's own
 * docstring for why: the Map tab is this app's highest-traffic surface),
 * scoped to the SAME shared filters/search/layers every other Map-tab
 * surface reads.
 *
 * Per explicit product direction, this card shows ONLY Leads and Projects
 * (Workforce/Subcontractor/At-Risk-SLA have no backing data model here, the
 * same reasoning the KPI row and map legend already apply), split into two
 * equal-width halves rather than the reference mockup's four equal columns
 * — each half independently guaranteed up to its own latest 2 events, so
 * one entity type being more active can never crowd the other out (see the
 * backend's own `_recent_activity_events_for_entity` docstring).
 */
const RecentActivityInView = ({
  onSelectEvent,
  onViewAll
}: RecentActivityInViewProps) => {
  const { t } = useTranslation()
  const { data, status, error, refetch } = useRecentActivityInView()

  const isLoading = status === "loading"
  const isError = status === "error"
  const isEmpty =
    status === "idle" &&
    data !== null &&
    data.lead.length === 0 &&
    data.project.length === 0

  return (
    <div
      className={`shrink-0 p-4 ${GLOBAL_MAP_SURFACE_CLASS}`}
      data-testid="global-map-recent-activity"
    >
      <Flex justify="space-between" align="center" className="mb-3">
        <Title
          level={5}
          className="!mb-0 !text-[15px] font-semibold !text-[#0F172A]"
        >
          {t("globalMap.recentActivity.title")}
        </Title>
        <button
          type="button"
          onClick={onViewAll}
          className="cursor-pointer border-none bg-transparent p-0 text-[13px] font-semibold text-primary hover:underline"
        >
          {t("globalMap.recentActivity.viewAll")}
        </button>
      </Flex>

      {isLoading && <Skeleton active paragraph={{ rows: 2 }} title={false} />}

      {isError && (
        <div className="flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-slate-200 py-6">
          <Text type="secondary" className="text-xs">
            {t("globalMap.recentActivity.loadError")}
          </Text>
          {error?.code && (
            <Text type="secondary" className="text-[11px]">
              {error.code}
            </Text>
          )}
          <Button size="small" onClick={refetch} className="mt-1">
            {t("globalMap.recentActivity.retry")}
          </Button>
        </div>
      )}

      {isEmpty && (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-dashed border-[#CBD5E1] bg-slate-50/40 py-8">
          <span
            aria-hidden
            className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-blue-50 text-[18px] text-blue-500"
          >
            <Icon type="Files" />
          </span>
          <Text className="text-[13px] text-slate-500">
            {t("globalMap.recentActivity.empty")}
          </Text>
        </div>
      )}

      {!isLoading && !isError && !isEmpty && data && (
        <div className="flex flex-col gap-4 sm:flex-row sm:divide-x sm:divide-slate-200">
          <EntityColumn
            entityType="lead"
            heading={t("globalMap.entityLabel.lead")}
            emptyLabel={t("globalMap.recentActivity.emptyLead")}
            events={data.lead}
            onSelectEvent={onSelectEvent}
          />
          <EntityColumn
            entityType="project"
            heading={t("globalMap.entityLabel.project")}
            emptyLabel={t("globalMap.recentActivity.emptyProject")}
            events={data.project}
            onSelectEvent={onSelectEvent}
          />
        </div>
      )}
    </div>
  )
}

export default RecentActivityInView
